import * as R from 'ramda'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import 'moment/locale/th'
import 'moment/locale/es'
import 'moment/locale/ru'

import { getLocale } from 'app/translations/intl-context'
import { enUS, es, ru, th } from 'date-fns/locale'

export const dateRegExpFormat = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/

export const isDateValid = (input) => {
  return dateRegExpFormat.test(input)
}

export const dateFormat = 'MMM D, YYYY'
export const dateGeneralFormat = 'YYYY-MM-DD'
export const dateDatePickerFormat = 'MMMM dd, yyyy'
export const timeFormat = 'h:mm A'
export const eventDateFormat = 'ddd, D MMM'
export const voucherDateFormat = 'ddd, D MMM'
export const chatDateFormat = 'ddd, MMM D'
export const chatTimeFormat = 'h:mm A'
export const mobilePreviewDateFormat = 'MMM DD, YYYY'
export const mobilePreviewTimeFormat = 'h:mm A'

// Intl
export const localDateTimeFormat = {
  // Oct 24, 2025
  // 24 ต.ค. 2568
  DATE: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  // Oct 24
  // 24 ต.ค.
  DATE_SHORT: {
    month: 'short',
    day: 'numeric',
  },
  // 10:00 AM
  // 10:00
  TIME: {
    hour: '2-digit',
    minute: '2-digit',
  },
  // October 24, 2025
  // 24 ตุลาคม 2568
  DATE_FULL: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  // Oct 24, 2025 10:00 AM
  // 24 ต.ค. 2568 10:00
  DATE_TIME: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  // October 24, 2025 10:00 AM
  // 24 ตุลาคม 2568 10:00
  DATE_TIME_FULL: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  // Friday, Oct 24 10:00 AM
  // วันศุกร์ที่ 24 ต.ค. 2568 10:00
  DATE_TIME_FULL_DAY: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  },
  // 2025
  // 2568
  YEAR: {
    year: 'numeric',
  },
}

export const getDifferenceDays = ({ start, end }) =>
  moment(end).diff(start, 'days')

export const formatDate = (date) => {
  if (!date) {
    return ''
  }
  const m = moment(date)
  return getLocalizedDate(m.toDate(), 'DATE')
}

export const getDateAndTime = (dateTimeStr) => {
  const date = moment(dateTimeStr).format(dateFormat)
  const time = moment(dateTimeStr).format(timeFormat)
  return { date, time }
}

export const getDateAndTimeAndTimezone = (dateTimeStr, zone) => {
  const date = R.isNil(zone)
    ? moment().format(dateFormat)
    : momentTimezone(dateTimeStr).tz(zone).format(dateFormat)
  const time = R.isNil(zone)
    ? moment().format(timeFormat)
    : momentTimezone(dateTimeStr).tz(zone).format(timeFormat)
  const offsets = momentTimezone.tz(zone).format('Z')

  return { date, time, offsets }
}

export const getDateAndTimeUTC = (dateTimeStr) => {
  const date = moment(dateTimeStr).utc().format(dateFormat)
  const time = moment(dateTimeStr).utc().format(timeFormat)
  return { date, time }
}

export const getConversationDateTime = (dateStr) => {
  let mom = moment.utc(dateStr, 'YYYY-MM-DD HH:mm:ss.SSS').local()
  const formattedDate = getLocalizedDate(
    mom.toISOString(),
    'DATE_TIME_FULL_DAY',
  )
  return formattedDate
}

// to do arg isFromNow = false)
export const getDateFromNow = (date, local) => {
  return date.locale(local).fromNow(true)
}

export const getHumanizeDate = (date) => {
  let humanDate = ''
  if (date) {
    let diff = moment.duration(moment().diff(date))
    humanDate = diff.humanize()
    if (Math.ceil(diff.asHours()) > 23) {
      humanDate = getLocalizedDate(date, 'DATE_TIME')
    }
  }
  return humanDate
}

export const getRangDate = (startDate, endDate) => {
  const isDupYear = moment(startDate).isSame(endDate, 'year')

  const dupYearLable = `${moment(startDate).format('MMM DD')} - ${moment(
    endDate,
  ).format('MMM DD,')} ${moment(endDate).format('YYYY')}`
  const diffYearLable = `${moment(startDate).format(dateFormat)} - ${moment(
    endDate,
  ).format(dateFormat)}`

  return isDupYear ? dupYearLable : diffYearLable
}

export function timeAgo(date) {
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.round(diffMs / 60000)

  const locale = getLocale()
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (diffMins < 1) return rtf.format(0, 'second')
  if (diffMins < 60) return rtf.format(-diffMins, 'minute')

  const diffHours = Math.round(diffMins / 60)
  if (diffHours < 24) return rtf.format(-diffHours, 'hour')

  const diffDays = Math.round(diffHours / 24)
  return rtf.format(-diffDays, 'day')
}

export function getLocalizedDate(date, format = 'DATE_TIME') {
  if (!date) {
    return ''
  }

  const locale = getLocale()

  // check if date is a moment object
  if (moment.isMoment(date)) {
    date = date.toDate()
  }

  return new Intl.DateTimeFormat(locale, localDateTimeFormat[format]).format(
    new Date(date),
  )
}

export const getDatePickerDisplayFormat = () => {
  const appLocale = getLocale()
  const displayFormat = {
    th: 'd MMMM yyyy',
    ru: 'd MMMM yyyy',
    es: 'd MMMM yyyy',
    en: 'MMMM d, yyyy',
  }
  return displayFormat[appLocale]
}

export const getDateFnsLocale = () => {
  const appLocale = getLocale()
  const locale = {
    th: th,
    ru: ru,
    es: es,
    en: enUS,
  }
  return locale[appLocale] || enUS
}

export const getLocalizedDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return ''
  }
  const isDupYear = moment(startDate).isSame(endDate, 'year')

  const dupYearLabel = `${getLocalizedDate(startDate, 'DATE_SHORT')} - ${getLocalizedDate(endDate, 'DATE')}`
  const diffYearLabel = `${getLocalizedDate(startDate, 'DATE')} - ${getLocalizedDate(endDate, 'DATE')}`

  return isDupYear ? dupYearLabel : diffYearLabel
}
